// /home/szymon/kreator2/frontend/src/components/OrdersPanel.js
// v2.5.0 – Dodano Popup, ikony kopiowania, węższe popupy (compact)

import React, { useState, useEffect, useCallback, useRef } from 'react';
// ZMIANA: Importujemy useSearchParams (dla paginacji) i useLocation
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
// ZMIANA: Importujemy debounce z lodash
import debounce from 'lodash.debounce';
// Importujemy ikonę kopiowania z lucide-react
import { ClipboardCopy } from 'lucide-react';
// Importujemy komponent Popup
import Popup from './common/Popup';

const SESSION_STORAGE_KEY = 'adminOrdersSearchTerm'; // Klucz dla sessionStorage

export default function OrdersPanel({ backendUrl }) {
  const navigate = useNavigate();
  const location = useLocation(); // Potrzebne do zachowania ?tab=

  // --- Stan ---
  const [ordersData, setOrdersData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showAddOrderForm, setShowAddOrderForm] = useState(false);
  const [newOrderName, setNewOrderName] = useState('');
  const [newOrderEmail, setNewOrderEmail] = useState('');
  
  // Stan dla Popup
  const [popupConfig, setPopupConfig] = useState(null);

  // ZMIANA: Stan dla pola wyszukiwania, inicjalizowany z sessionStorage
  const [searchTerm, setSearchTerm] = useState(
    () => sessionStorage.getItem(SESSION_STORAGE_KEY) || ''
  );

  // Stan dla paginacji (z URL)
  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const pageSize = 50;

  // --- Pobieranie danych ---
  const fetchOrders = useCallback(async (pageToFetch, searchToFetch) => {
    setError('');
    setLoading(true);

    const existingParams = new URLSearchParams(location.search);
    const currentTab = existingParams.get('tab') || 'orders';

    const url = `${backendUrl}/admin/orders?page=${pageToFetch}&search=${encodeURIComponent(searchToFetch)}&tab=${currentTab}`;
    console.log('[OrdersPanel] => fetch =>', url);

    try {
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) throw new Error('Błąd pobierania zamówień.');
      const data = await res.json();
      setOrdersData(data.orders || []);
      setTotalCount(data.totalCount || 0);
    } catch (err) {
      console.error(err);
      setError('Nie udało się pobrać zamówień.');
      setOrdersData([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [backendUrl, location.search]);

  // ZMIANA: Debounced fetch function - używamy useRef, aby uniknąć tworzenia nowej funkcji debounce przy każdym renderze
  const debouncedFetchRef = useRef(
    debounce((page, term) => {
      fetchOrders(page, term);
    }, 400) // Opóźnienie 400ms
  );

  // ZMIANA: Główny useEffect reaguje na zmianę searchTerm i currentPage
  useEffect(() => {
    // Wywołaj debounced fetch. Debounce sam zarządza anulowaniem poprzednich wywołań.
    debouncedFetchRef.current(currentPage, searchTerm);

    // Zapisz searchTerm do sessionStorage przy każdej zmianie
    sessionStorage.setItem(SESSION_STORAGE_KEY, searchTerm);

    // Jeśli searchTerm się zmienił, a strona nie jest pierwsza, zresetuj paginację w URL
    // Ale rób to tylko jeśli faktycznie się zmienił searchTerm (nie tylko currentPage)
    // Można to zrobić w `handleSearchChange` dla uproszczenia.

  }, [searchTerm, currentPage, fetchOrders]); // Reaguj na searchTerm i currentPage

  // --- Handlery UI ---

  // ZMIANA: Handler zmiany w inpucie wyszukiwania
  const handleSearchChange = (e) => {
    const newSearchTerm = e.target.value;
    setSearchTerm(newSearchTerm);

    // Przy zmianie wyszukiwania, resetuj stronę do 1 w URL
    const newParams = new URLSearchParams(searchParams);
    if (parseInt(newParams.get('page') || '1', 10) !== 1) {
        newParams.set('page', '1');
        // Używamy replace, aby nie zaśmiecać historii przeglądarki każdą literą
        setSearchParams(newParams, { replace: true });
    }
  };


  const handleAddOrder = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      // ... (logika dodawania bez zmian) ...
      const response = await fetch(`${backendUrl}/admin/orders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ orderName: newOrderName, customOrderId: newOrderEmail }),
      });
      if (!response.ok) throw new Error('Błąd tworzenia zamówienia.');

      setNewOrderName('');
      setNewOrderEmail('');
      setShowAddOrderForm(false);

      // Po dodaniu, czyścimy wyszukiwanie i resetujemy stronę
      setSearchTerm(''); // Wyczyść stan
      sessionStorage.removeItem(SESSION_STORAGE_KEY); // Wyczyść sessionStorage
      const newParams = new URLSearchParams(searchParams);
      newParams.set('page', '1');
      setSearchParams(newParams, { replace: true }); // Resetuj URL

    } catch (err) {
      console.error(err);
      setError('Nie udało się dodać zamówienia.');
    } finally {
      setLoading(false);
    }
  };

  const handleOrderClick = (orderId) => {
    navigate(`/admin/orders/${orderId}`); // Nawigacja bez zmian
  };

  const handleCopyPublicLink = async (e, publicAccessKey) => {
    e.stopPropagation();
    try {
      const link = `${window.location.origin}/client/orders/${publicAccessKey}`;
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(link);
        setPopupConfig({
          type: 'success',
          title: 'Sukces',
          message: `Skopiowano link do schowka`,
          size: 'compact',
          confirmText: 'OK'
        });
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = link;
        textArea.style.position = 'fixed'; textArea.style.top = '-999px';
        document.body.appendChild(textArea);
        textArea.focus(); textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        setPopupConfig({
          type: 'success',
          title: 'Sukces',
          message: `Skopiowano link do schowka`,
          size: 'compact',
          confirmText: 'OK'
        });
      }
    } catch (err) {
      console.error('Błąd kopiowania linku:', err);
      setPopupConfig({
        type: 'error',
        title: 'Błąd',
        message: 'Nie udało się skopiować linku.',
        size: 'compact',
        confirmText: 'OK'
      });
    }
  };

  const handleCopyOrderName = async (e, orderName) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(orderName);
      setPopupConfig({
        type: 'success',
        title: 'Sukces',
        message: `Skopiowano nazwę zamówienia`,
        size: 'compact',
        confirmText: 'OK'
      });
    } catch (err) {
      console.error('Błąd kopiowania:', err);
      setPopupConfig({
        type: 'error',
        title: 'Błąd',
        message: 'Nie udało się skopiować.',
        size: 'compact',
        confirmText: 'OK'
      });
    }
  };

  const handleCopyEmail = async (e, email) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(email);
      setPopupConfig({
        type: 'success',
        title: 'Sukces',
        message: `Skopiowano email`,
        size: 'compact',
        confirmText: 'OK'
      });
    } catch (err) {
      console.error('Błąd kopiowania:', err);
      setPopupConfig({
        type: 'error',
        title: 'Błąd',
        message: 'Nie udało się skopiować.',
        size: 'compact',
        confirmText: 'OK'
      });
    }
  };

  // Paginacja aktualizuje URL
  const totalPages = Math.ceil(totalCount / pageSize);

  const handlePrevPage = () => {
    if (currentPage > 1) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set('page', (currentPage - 1).toString());
      setSearchParams(newParams); // Użyj setSearchParams do zmiany URL
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set('page', (currentPage + 1).toString());
      setSearchParams(newParams); // Użyj setSearchParams do zmiany URL
    }
  };

  return (
    <div className="orders-container">
      <h3>Lista Zamówień</h3>
      {error && <p className="dashboard-error">{error}</p>}

      {/* ZMIANA: Input wyszukiwania bez formularza, obsługiwany przez onChange */}
      <div className="orders-search">
        <label htmlFor="searchInput">Wyszukaj:</label>
        <input
          id="searchInput"
          type="text"
          value={searchTerm} // Powiązany ze stanem searchTerm
          onChange={handleSearchChange} // Używamy nowego handlera
          placeholder="Nazwa lub e-mail..."
          className="orders-input"
        />
      </div>

      <button
        className="orders-add-btn"
        onClick={() => setShowAddOrderForm((prev) => !prev)}
      >
        {showAddOrderForm ? 'Anuluj' : 'Dodaj zamówienie'}
      </button>

      {showAddOrderForm && (
        <form onSubmit={handleAddOrder} className="orders-add-form">
          {/* ... (formularz dodawania bez zmian) ... */}
          <div className="orders-form-group">
            <label>Nazwa zamówienia:</label>
            <input type="text" value={newOrderName} onChange={(e) => setNewOrderName(e.target.value)} required className="orders-input" />
          </div>
          <div className="orders-form-group">
            <label>Email klienta:</label>
            <input type="email" value={newOrderEmail} onChange={(e) => setNewOrderEmail(e.target.value)} required className="orders-input" />
          </div>
          <button type="submit" className="orders-button" disabled={loading}>
            {loading ? 'Zapisywanie...' : 'Zapisz'}
          </button>
        </form>
      )}

      {loading && searchTerm && <p>Wyszukiwanie...</p>}
      {loading && !searchTerm && <p>Wczytywanie zamówień...</p>}

      {!loading && ordersData.length === 0 && !error ? (
        <p>Brak zamówień{searchTerm ? ` pasujących do "${searchTerm}"` : ''}.</p>
      ) : (
        !loading && ordersData.length > 0 && (
          <table className="orders-table">
            {/* ... (tabela bez zmian) ... */}
            <thead>
              <tr>
                <th>ID</th><th>Nazwa</th><th>E-mail</th><th>Data utworzenia</th><th>Plik PDF</th><th>Link Klienta</th>
              </tr>
            </thead>
            <tbody>
              {ordersData.map((order) => {
                let pdfName = '';
                if (Array.isArray(order.files)) {
                  const pdfFile = order.files.find((f) => f.fileName?.toLowerCase().endsWith('.pdf'));
                  pdfName = pdfFile?.fileName || '';
                }
                const createdDate = order.createdAt ? new Date(order.createdAt).toLocaleString() : '-';
                return (
                  <tr key={order.id} onClick={() => handleOrderClick(order.id)} className="orders-row">
                    <td>{order.id}</td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span>{order.orderName}</span>
                        <button 
                          onClick={(e) => handleCopyOrderName(e, order.orderName)}
                          className="orders-copy-icon-btn"
                          title="Kopiuj nazwę"
                        >
                          <ClipboardCopy size={14} />
                        </button>
                      </div>
                    </td>
                    <td>
                      {order.customOrderId ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span>{order.customOrderId}</span>
                          <button 
                            onClick={(e) => handleCopyEmail(e, order.customOrderId)}
                            className="orders-copy-icon-btn"
                            title="Kopiuj email"
                          >
                            <ClipboardCopy size={14} />
                          </button>
                        </div>
                      ) : '-'}
                    </td>
                    <td>{createdDate}</td>
                    <td>{pdfName || '-'}</td>
                    <td style={{ textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                      <button onClick={(e) => handleCopyPublicLink(e, order.publicAccessKey)} style={{ cursor: 'pointer' }}>Kopiuj link</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )
      )}

      {/* Paginacja */}
      {!loading && totalPages > 1 && (
        <div style={{ marginTop: 15, display: 'flex', gap: 10, alignItems: 'center' }}>
          <button onClick={handlePrevPage} disabled={currentPage <= 1} className="orders-button">
            Poprzednia
          </button>
          <span>
            Strona <strong>{currentPage}</strong> z <strong>{totalPages}</strong> (Wyników: {totalCount})
          </span>
          <button onClick={handleNextPage} disabled={currentPage >= totalPages} className="orders-button">
            Następna
          </button>
        </div>
      )}
      
      {/* Popup */}
      {popupConfig && (
        <Popup
          config={popupConfig}
          onClose={() => setPopupConfig(null)}
        />
      )}
    </div>
  );
}